import React from "react";
import { Stack, Typography, Box, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch } from "../redux/store";
import { updateCompany } from "../redux/actions/companyActions";
import { cleanFormData } from "../helpers/formHelpers";
import { CompanyResponseDto } from "../shared/dtos/company/CompanyResponseDto";
import { companyActions } from "../redux/slices/company";

const companySchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  contactEmail: Yup.string().email("Invalid").required("Required"),
  contactPhone: Yup.string().required("Required"),
  headOfficeAddress: Yup.object().shape({
    streetLine1: Yup.string().required("Required"),
    city: Yup.string().required("Required"),
    stateOrProvince: Yup.string().required("Required"),
    postalCode: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
  }),
});

const UpdateCompanyForm: React.FC<{
  company: CompanyResponseDto;
  loading: boolean;
  error: string | null;
}> = ({ company, loading = false, error = null }) => {
  const dispatch = useAppDispatch();

  const initialValues = {
    name: company.name,
    contactEmail: company.contactEmail,
    contactPhone: company.contactPhone,
    website: company.website || "",
    taxId: company.taxId || "",
    headOfficeAddress: company.headOfficeAddress,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(companyActions.setErrorUpdatingCompany(null))}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={companySchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values);
          dispatch(updateCompany({ id: company.id, values: payload }));
        }}
      >
        {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={4}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: "text.secondary" }}
                >
                  Basic Information
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 1,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  }}
                >
                  <FormTextField name="name" label="Company Name" />
                  <FormTextField name="taxId" label="Tax ID / PIN" />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormTextField name="contactEmail" label="Email" />
                  <FormTextField name="contactPhone" label="Phone" />
                  <FormTextField name="website" label="Website" />
                </Box>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.5),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, mb: 2, display: "block" }}
                >
                  Head Office Location
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="headOfficeAddress.streetLine1"
                    label="Street"
                  />
                  <FormTextField
                    name="headOfficeAddress.streetLine2"
                    label="Street 2"
                  />
                  <FormTextField name="headOfficeAddress.city" label="City" />
                  <FormTextField
                    name="headOfficeAddress.stateOrProvince"
                    label="State"
                  />
                  <FormTextField
                    name="headOfficeAddress.postalCode"
                    label="Postal Code"
                  />
                  <FormTextField
                    name="headOfficeAddress.country"
                    label="Country"
                  />
                </Box>
              </Box>

              <SubmitButton
                title="Save Company Details"
                loading={loading}
                disabled={!dirty || !isValid}
                startIcon={<SaveOutlinedIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default UpdateCompanyForm;
